/**
 * Cart Drawer Free Gift Component
 *
 * Handles add-to-cart for free gift products inside the cart drawer.
 * Supports two flows:
 * 1. Direct add — single-variant products with data-variant-id
 * 2. Modal open — multi-variant products with data-open-modal
 *
 * Dispatches a CartAddEvent-compatible CustomEvent so cart-items-component
 * picks up the change and re-renders the drawer via the section rendering API.
 */


import { announceCartLinesUpdate } from '@theme/cart-events';
class CartDrawerFreeGift extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick.bind(this));
  }

  /**
   * @param {MouseEvent} event
   */
  async #handleClick(event) {
    const modalButton = event.target.closest('[data-open-modal]');

    if (modalButton && !modalButton.disabled) {
      const productId = Number(modalButton.dataset.productId);

      if (!productId) {
        return;
      }

      const productData = this.#getProductData(productId);

      if (!productData) {
        return;
      }

      const modal = this.querySelector('cart-drawer-free-gift-modal');

      if (modal) {
        modal.open(productData);
      }

      return;
    }

    const button = event.target.closest('[data-variant-id]');

    if (!button || button.disabled) {
      return;
    }

    const variantId = Number(button.dataset.variantId);

    if (!variantId) {
      return;
    }

    button.classList.add('is-loading');
    const originalText = button.textContent;
    button.textContent = '...';
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { '_is_free_gift': 'true' } }],
          sections: this.#getSectionIds(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add item');
      }

      const data = await response.json();

      await announceCartLinesUpdate({
        action: 'add',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        source: 'cart-drawer-free-gift',
        sourceId: 'cart-drawer-free-gift',
        sections: data.sections,
        // `/cart/add.js` returns the added line items with their merged quantity — enough for
        // `detail.items`, so the announcement can resolve without a second round trip.
        items: data.items,
        variantId: String(variantId),
      });

      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 1200);
    } catch (error) {
      console.error('[cart-drawer-free-gift]', error.message);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  /**
   * Parse the embedded JSON blob and return data for the given product ID.
   * @param {number} productId
   * @returns {Object|null}
   */
  #getProductData(productId) {
    const jsonScript = this.querySelector('script[data-free-gift-products]');

    if (!jsonScript) {
      return null;
    }

    try {
      const products = JSON.parse(jsonScript.textContent);
      return products.find((p) => p.id === productId) || null;
    } catch (error) {
      console.error('[cart-drawer-free-gift] Failed to parse product data', error);
      return null;
    }
  }

  /**
   * Get section IDs for section rendering API.
   * Cart items component listens for sections to update the drawer.
   * @returns {string}
   */
  #getSectionIds() {
    const cartItemsComponent = this.closest('cart-items-component');

    if (cartItemsComponent) {
      const sectionId = cartItemsComponent.dataset.sectionId;

      if (sectionId) {
        return sectionId;
      }
    }

    return '';
  }
}

if (!customElements.get('cart-drawer-free-gift')) {
  customElements.define('cart-drawer-free-gift', CartDrawerFreeGift);
}
